import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { AlertCircle, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "@/components/ui/table";
import { SearchFilterBar } from "@/components/shared/SearchFilterBar";
import { BulkActionBar } from "@/components/shared/BulkActionBar";
import { StickyToolbarProvider } from "@/hooks/useStickyToolbar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useViewerReadOnly } from "@/contexts/ViewerReadOnlyContext";
import { useAppData } from "@/contexts/AppDataContext";
import { rankTrackerApi } from "@/resources/rank-tracker";
import { toast } from "@/hooks/use-toast";
import type { DailyRanksProduct, DailyRanksResponse, DailyRanksTheme } from "@/types/rankTracker";
import type { RankDetailTarget } from "@/components/rank-tracker/KeywordRankDetailModal";
import { HeatmapHeaderRow, type ProductSortDirection } from "./HeatmapHeaderRow";
import { ProductRow } from "./ProductRow";
import { ThemeRow } from "./ThemeRow";
import { KeywordRow } from "./KeywordRow";
import { DEFAULT_CUTOFFS, type AdPushCutoffs } from "./adPush";
import {
  RankTypeToggle,
  useRankTypePreference,
  readCalloutSeen,
  writeCalloutSeen,
  LAUNCH_DATE,
} from "./RankTypeToggle";
import { RankLegend } from "./RankLegend";
import {
  FilterDropdown,
  buildThemeFilterOptions,
  buildAdvertisingFilterOptions,
  computeRankMovementBucket,
  THEME_FILTER_COLUMN_ID,
  MOVEMENT_FILTER_COLUMN_ID,
  ADVERTISING_FILTER_COLUMN_ID,
  ADVERTISED_FILTER_VALUE,
  NOT_ADVERTISED_FILTER_VALUE,
  NO_THEME_FILTER_VALUE,
  type RankMovementBucket,
} from "./FilterDropdown";

/**
 * The heat table's column count -- label, chart, KPS, Search Vol., Kw. Sales, Comp., Rel., Ad push
 * (AUT-497 slice B), one per day, Date Added. Every fallback row (error/loading/empty) spans this so
 * it lines up under the real header (`rows.test.tsx` asserts the same `9 + days.length` on the row
 * components themselves).
 */
function totalColumnCount(dayCount: number): number {
  return 9 + dayCount;
}

/**
 * Splits an ISO calendar date `YYYY-MM-DD` into its numeric parts -- never `new Date(iso)`, which
 * parses as UTC midnight and draws the previous day west of Greenwich
 * (`components/rank-tracker.md § Target state -- the screen`). A private copy: this file cannot
 * import `./constants`'s unexported helper of the same shape without editing that WEB-005 file,
 * which is outside this task's freeze.
 */
function splitIsoDate(iso: string): { year: number; month: number; day: number } {
  const [year, month, day] = iso.split("-").map((part) => Number(part));
  return { year, month, day };
}

/**
 * Every calendar day from `from` to `to` inclusive, oldest first -- mirrors the backend's day axis
 * so the header (and its day columns) render before any response arrives (FR-13, the Loading
 * acceptance criterion). Built from local Date arithmetic on numeric y/m/d parts, never by parsing
 * the ISO strings directly.
 */
function enumerateDays(from: string, to: string): string[] {
  const start = splitIsoDate(from);
  const end = splitIsoDate(to);
  const cursor = new Date(start.year, start.month - 1, start.day);
  const stop = new Date(end.year, end.month - 1, end.day);
  const days: string[] = [];
  while (cursor.getTime() <= stop.getTime()) {
    const y = cursor.getFullYear();
    const m = String(cursor.getMonth() + 1).padStart(2, "0");
    const d = String(cursor.getDate()).padStart(2, "0");
    days.push(`${y}-${m}-${d}`);
    cursor.setDate(cursor.getDate() + 1);
  }
  return days;
}

function textIncludes(haystack: string, needleLower: string): boolean {
  return haystack.toLowerCase().includes(needleLower);
}

/**
 * The search-matching rule, `sources.md § 5.3`: a product-name or ASIN match keeps the whole
 * product; a theme-name match keeps the whole theme; a keyword-only match narrows the theme to the
 * matching keywords, drops themes that end up empty and drops products that end up with no themes.
 * A theme's own `positions`/`counts` are never recomputed here -- narrowing only changes which
 * keyword rows are visible, not a roll-up value.
 */
function filterProducts(products: DailyRanksProduct[], query: string): DailyRanksProduct[] {
  const q = query.trim().toLowerCase();
  if (!q) return products;

  const result: DailyRanksProduct[] = [];
  for (const product of products) {
    if (textIncludes(product.name, q) || textIncludes(product.asin, q)) {
      result.push(product);
      continue;
    }

    const themes: DailyRanksTheme[] = [];
    for (const theme of product.themes) {
      if (textIncludes(theme.name, q)) {
        themes.push(theme);
        continue;
      }
      const keywords = theme.keywords.filter((keyword) => textIncludes(keyword.keyword, q));
      if (keywords.length > 0) {
        themes.push({ ...theme, keywords });
      }
    }
    if (themes.length > 0) {
      result.push({ ...product, themes });
    }
  }
  return result;
}

/**
 * WEB-009's + AUT-440's three filter groups, AND-ed with each other and applied on top of whatever
 * `search` (the function above) already narrowed. A `Theme` selection drops every non-matching theme
 * outright (its keywords never render); a `Rank movement` selection narrows a surviving theme's own
 * keyword list to the matching buckets, excluding a keyword with fewer than two known days regardless
 * of which buckets are active (`sources.md § 5.3:374`); an `Advertising` selection narrows to
 * keywords whose `hasAdSpend` matches a selected option (`advertised` ⇒ `hasAdSpend`,
 * `not-advertised` ⇒ `!hasAdSpend`) -- selecting both is the same as selecting neither
 * (`sources.md § 5.7`). A theme or product left with no keywords is dropped, so `Advertised` collapses
 * the tree to only the branches that still contain such a keyword.
 *
 * Never touches a `positions`/`counts` value at any level -- only which keyword rows are present,
 * so a product's and a theme's own roll-up stays exactly what the server sent.
 */
function applyRankHeatmapFilters(
  products: DailyRanksProduct[],
  activeThemeIds: Set<string>,
  activeMovementBuckets: Set<RankMovementBucket>,
  activeAdvertising: Set<string>,
): DailyRanksProduct[] {
  if (activeThemeIds.size === 0 && activeMovementBuckets.size === 0 && activeAdvertising.size === 0)
    return products;

  const result: DailyRanksProduct[] = [];
  for (const product of products) {
    const themes: DailyRanksTheme[] = [];
    for (const theme of product.themes) {
      const themeKey = theme.themeId === null ? NO_THEME_FILTER_VALUE : String(theme.themeId);
      if (activeThemeIds.size > 0 && !activeThemeIds.has(themeKey)) continue;

      let keywords = theme.keywords;
      if (activeMovementBuckets.size > 0) {
        keywords = keywords.filter((keyword) => {
          const bucket = computeRankMovementBucket(keyword.positions);
          return bucket !== null && activeMovementBuckets.has(bucket);
        });
      }
      if (activeAdvertising.size > 0) {
        keywords = keywords.filter((keyword) =>
          activeAdvertising.has(keyword.hasAdSpend ? ADVERTISED_FILTER_VALUE : NOT_ADVERTISED_FILTER_VALUE),
        );
      }
      if (keywords.length === 0) continue;

      themes.push(keywords === theme.keywords ? theme : { ...theme, keywords });
    }
    if (themes.length > 0) {
      result.push({ ...product, themes });
    }
  }
  return result;
}

/** Products only, by name -- case-insensitive (`components/rank-tracker.md § Target state -- the
 * screen`, `sources.md § 6`). Themes and keywords always keep the order the server sent. */
function sortProducts(
  products: DailyRanksProduct[],
  direction: ProductSortDirection,
): DailyRanksProduct[] {
  if (direction === null) return products;
  const sorted = [...products].sort((a, b) =>
    a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  );
  return direction === "desc" ? sorted.reverse() : sorted;
}

/** The one sortable column's 3-state cycle: unsorted -> desc -> asc -> unsorted. From the initial
 * `asc` the next click clears the sort, exactly as the captured design's `getNextSort` behaves. */
function nextSortDirection(current: ProductSortDirection): ProductSortDirection {
  if (current === null) return "desc";
  if (current === "desc") return "asc";
  return null;
}

const LOADING_SKELETON_ROW_COUNT = 5;

/**
 * A reachable horizontal-scroll affordance for a table that can be far taller than the viewport --
 * without it the browser's native horizontal scrollbar sits at the very bottom of the (possibly very
 * long) table and is unreachable without scrolling to the end first (FR-10). `sticky bottom-8` pins
 * it near the bottom of the viewport for as long as any part of the scrollable table is on screen,
 * the same mechanism `ABTestTable`'s `ScrollArrows` already ships for the same problem on a similarly
 * wide table -- inlined here rather than imported cross-domain because this task's freeze permits
 * only this file and its test.
 */
function TableScrollArrows({
  canScrollLeft,
  canScrollRight,
  onScrollLeft,
  onScrollRight,
}: {
  canScrollLeft: boolean;
  canScrollRight: boolean;
  onScrollLeft: () => void;
  onScrollRight: () => void;
}) {
  if (!canScrollLeft && !canScrollRight) return null;

  return (
    <div
      aria-hidden
      className="pointer-events-none sticky bottom-8 left-0 right-0 z-20 flex h-0 items-center justify-between px-2"
    >
      <button
        type="button"
        onClick={onScrollLeft}
        aria-label="Scroll table left"
        tabIndex={canScrollLeft ? 0 : -1}
        className={cn(
          "pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-xl transition hover:bg-secondary hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          !canScrollLeft && "invisible",
        )}
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={onScrollRight}
        aria-label="Scroll table right"
        tabIndex={canScrollRight ? 0 : -1}
        className={cn(
          "pointer-events-auto inline-flex h-9 w-9 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-xl transition hover:bg-secondary hover:shadow-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          !canScrollRight && "invisible",
        )}
      >
        <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}

export interface RankHeatmapTableProps {
  /** Resetting search and expansion when this changes is this component's job -- selection and
   * filters are WEB-009's. */
  brandId: number | null | undefined;
  /** The requested window, echoed back so the header's day columns render before any response
   * arrives (FR-13, the Loading acceptance criterion). Once `data` arrives its own `days` is used --
   * the two should always agree, `data.days` is simply the one actually painted. */
  dateFrom: string;
  dateTo: string;
  data: DailyRanksResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => void;
  /** WEB-010 (design-conformance pass, items 3+6): the window-range control, built and owned by
   * `RankTracker.tsx` (`selectedDays` drives `useGetDailyRanks` there) and handed down already
   * rendered so this component renders it in the toolbar row without knowing anything about its
   * presets or state. Optional only so `index.test.tsx`'s existing fixtures keep working unchanged;
   * the one real call site always passes it. */
  windowPicker?: ReactNode;
  /** Fired from exactly three places -- a keyword row (by its chart button or by clicking the row
   * itself), a theme row's chart button, and a hero-product row's chart button -- each constructing
   * the `RankDetailTarget` arm for its own level right here (WEB-005: a theme's `themeId: null` is
   * the `No theme` bucket, not resolved by name or any other search), so no row can hand up another
   * row's view. `RankTracker.tsx` receives the already-built target and hands it straight to
   * `setOpenDetail`; this component owns no modal state of its own. */
  onOpenDetail?: (target: RankDetailTarget) => void;
}

/**
 * The heat table shell: composes WEB-005's product/theme/keyword rows and owns loading, error,
 * empty, sorting and the sticky frame (`components/rank-tracker.md § Target state -- the screen`).
 * It computes no aggregate of its own -- every `positions`/`counts` value is rendered exactly as the
 * response carries it. Selection, the bulk bar and the two filter groups are WEB-009's, in the same
 * file.
 */
export function RankHeatmapTable({
  brandId,
  dateFrom,
  dateTo,
  data,
  isLoading,
  isError,
  refetch,
  windowPicker,
  onOpenDetail,
}: RankHeatmapTableProps) {
  const [search, setSearch] = useState("");
  const [expandedProducts, setExpandedProducts] = useState<Set<number>>(new Set());
  const [expandedThemes, setExpandedThemes] = useState<Set<string>>(new Set());
  const [sortDirection, setSortDirection] = useState<ProductSortDirection>("asc");
  // WEB-009: selection is keyed by keyword id, independent of expandedProducts/expandedThemes so it
  // survives collapsing either (FR-11). `activeFilters` is the shared `Record<string, string[]>`
  // shape `FilterDropdown.tsx`'s composite already speaks, keyed by THEME_FILTER_COLUMN_ID and
  // MOVEMENT_FILTER_COLUMN_ID.
  const [selectedKeywordIds, setSelectedKeywordIds] = useState<Set<number>>(new Set());
  const [activeFilters, setActiveFilters] = useState<Record<string, string[]>>({});
  const [isBulkPending, setIsBulkPending] = useState(false);
  // WEB-004 / FR-6. The Organic / Sponsored / Both view. Owned here (WEB-003's module comment) and
  // threaded into the toggle, the legend and every row. Persisted per browser, not per brand, so it
  // is deliberately NOT part of the brand-switch reset effect below (J1's rank-type carve-out).
  const [rankType, setRankType] = useRankTypePreference();
  // AUT-497 slice C. The one-time "Sponsored rank is here" onboarding callout's state. `calloutSeen`
  // is seeded once from localStorage (try/catch, defaulting to shown-if-unseen — NFR-2); `dismissCallout`
  // both flips the in-memory flag and persists it, so the callout retires for good in this browser (FR-4).
  const [calloutSeen, setCalloutSeen] = useState<boolean>(readCalloutSeen);
  const dismissCallout = useCallback(() => {
    setCalloutSeen(true);
    writeCalloutSeen();
  }, []);
  // FR-3: if the screen loads with Both already selected, the callout never renders AND is marked seen,
  // so it will not appear on a later visit in a non-Both view either. Mount-only — it reads the initial
  // rankType/calloutSeen and must not re-fire when the user later switches views.
  useEffect(() => {
    if (rankType === "both" && !calloutSeen) {
      dismissCallout();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // AUT-497 slice B. The brand's committed ad-push cutoffs, read once per brand and threaded into
  // every keyword's Ad push pill (FR-8). Keyed on the brand server-side, so a brand switch re-reads
  // them (FR-12) without this component holding anything brand-specific of its own. Before they load
  // the column classifies over DEFAULT_CUTOFFS. WEB-004 will relabel over the editing draft; this
  // task only passes the committed values down.
  const { data: adPushCutoffsData } = rankTrackerApi.useGetAdPushCutoffs(brandId);
  const adPushCutoffs: AdPushCutoffs = adPushCutoffsData
    ? { easeOff: adPushCutoffsData.easeOff, hold: adPushCutoffsData.hold, push: adPushCutoffsData.push }
    : DEFAULT_CUTOFFS;
  const brandHasCustomCutoffs = adPushCutoffsData?.isCustom ?? false;
  // The Ad-push rules panel's open state and its live-edit draft (WEB-004). While the panel is open
  // and the draft is valid, the column relabels over the draft in the browser (D-6 — no network per
  // keystroke); otherwise it classifies over the committed cutoffs. The brand name feeds the panel's
  // title badge; resolved from the app's brand list since only `brandId` is threaded into the table.
  const [rulesOpen, setRulesOpen] = useState(false);
  const [adPushDraft, setAdPushDraft] = useState<AdPushCutoffs | null>(null);
  const effectiveAdPushCutoffs = rulesOpen && adPushDraft ? adPushDraft : adPushCutoffs;
  const { brands } = useAppData();
  const brandName = brands.find((b) => b.id === brandId)?.name ?? "";

  // AUT-497 slice C (FR-6, D-1): the callout stops showing for everyone 30 days past a single fixed
  // launch cutoff — `>=` so "at or past LAUNCH_DATE + 30d" no longer renders. FR-5: it is suppressed
  // (not dismissed) while the Ad-push rules panel is open, so it may reappear once that panel closes
  // if still unseen.
  const pastLaunchWindow = Date.now() - Date.parse(LAUNCH_DATE) >= 30 * 24 * 60 * 60 * 1000;
  const calloutOpen = !calloutSeen && !pastLaunchWindow && !rulesOpen;

  const viewerReadOnly = useViewerReadOnly();
  const { mutateAsync: deleteKeywordAsync } = rankTrackerApi.useDeleteRankTrackerKeyword();

  // FR-16 for the table itself: nothing from the previous brand survives a switch. Selection and
  // the two filter groups join the same reset (FR-16's own acceptance criterion for this task).
  useEffect(() => {
    setSearch("");
    setExpandedProducts(new Set());
    setExpandedThemes(new Set());
    setSelectedKeywordIds(new Set());
    setActiveFilters({});
    // FR-12: never carry the previous brand's rules-panel draft across a switch. The cutoffs are
    // brand-keyed server-side, so the panel re-seeds from the new brand's GET when reopened.
    setRulesOpen(false);
    setAdPushDraft(null);
  }, [brandId]);

  const days = data?.days ?? enumerateDays(dateFrom, dateTo);
  const totalColumns = totalColumnCount(days.length);

  const searchFilteredProducts = useMemo(
    () => filterProducts(data?.products ?? [], search),
    [data?.products, search],
  );

  const activeThemeIds = useMemo(
    () => new Set(activeFilters[THEME_FILTER_COLUMN_ID] ?? []),
    [activeFilters],
  );
  const activeMovementBuckets = useMemo(
    () => new Set((activeFilters[MOVEMENT_FILTER_COLUMN_ID] ?? []) as RankMovementBucket[]),
    [activeFilters],
  );
  const activeAdvertising = useMemo(
    () => new Set(activeFilters[ADVERTISING_FILTER_COLUMN_ID] ?? []),
    [activeFilters],
  );

  const visibleProducts = useMemo(
    () =>
      applyRankHeatmapFilters(
        searchFilteredProducts,
        activeThemeIds,
        activeMovementBuckets,
        activeAdvertising,
      ),
    [searchFilteredProducts, activeThemeIds, activeMovementBuckets, activeAdvertising],
  );

  // The `Theme` and `Advertising` filters' own option lists -- built off the full, unfiltered
  // response so they never jitter while the user types a search term or narrows another filter
  // group. The advertising counts are over every keyword and stay stable regardless of what else is
  // active (`sources.md § 5.7`).
  const themeFilterOptions = useMemo(
    () => buildThemeFilterOptions(data?.products ?? []),
    [data?.products],
  );
  const advertisingFilterOptions = useMemo(
    () => buildAdvertisingFilterOptions(data?.products ?? []),
    [data?.products],
  );

  // Resolves a selected keyword's own text for the bulk-untrack failure message, off the full
  // response so a keyword hidden by the current filters (but still selected) still has a name.
  const keywordTextById = useMemo(() => {
    const map = new Map<number, string>();
    for (const product of data?.products ?? []) {
      for (const theme of product.themes) {
        for (const keyword of theme.keywords) {
          map.set(keyword.id, keyword.keyword);
        }
      }
    }
    return map;
  }, [data?.products]);

  const sortedProducts = useMemo(
    () => sortProducts(visibleProducts, sortDirection),
    [visibleProducts, sortDirection],
  );
  const totalVisibleKeywords = useMemo(
    () =>
      sortedProducts.reduce(
        (sum, product) =>
          sum + product.themes.reduce((themeSum, theme) => themeSum + theme.keywords.length, 0),
        0,
      ),
    [sortedProducts],
  );

  const heroProductCount = data?.heroProductCount ?? 0;
  // FR-15's second sentence -- a hero product exists but nothing under it is tracked. Read off the
  // raw response, never off the filtered/sorted list, so a search term can never suppress it.
  const nothingTrackedYet = heroProductCount > 0 && (data?.productCount ?? 0) === 0;

  const toggleProduct = useCallback((id: number) => {
    setExpandedProducts((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleTheme = useCallback((key: string) => {
    setExpandedThemes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const toggleSort = useCallback(() => {
    setSortDirection((prev) => nextSortDirection(prev));
  }, []);

  // FR-11: a keyword's own checkbox.
  const toggleKeywordSelection = useCallback((keywordId: number, checked: boolean) => {
    setSelectedKeywordIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(keywordId);
      else next.delete(keywordId);
      return next;
    });
  }, []);

  // FR-11: a theme's checkbox selects/deselects every keyword currently listed under it (the ones
  // this render actually shows, after search and the two filter groups).
  const toggleThemeSelection = useCallback((keywordIds: number[], checked: boolean) => {
    setSelectedKeywordIds((prev) => {
      const next = new Set(prev);
      for (const id of keywordIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedKeywordIds(new Set()), []);

  // FR-21: no new backend route -- one DELETE /api/rank-tracker/keywords/{id} per selected keyword
  // through WEB-003's existing mutation (already wired to invalidate the daily-ranks cache on its
  // own onSuccess). Every call is issued regardless of another one failing -- Promise.all here never
  // rejects itself, since each entry catches its own -- so a failure partway through neither rolls
  // back nor abandons the remainder (a test in `selection.test.tsx` pins this). This inherits the
  // MIN(id) defect recorded in `components/rank-tracker.md § DELETE /api/rank-tracker/keywords/{id}`
  // and does not fix it: no second delete, no retry, no phrase-based fallback.
  const handleBulkStopTracking = useCallback(async () => {
    if (!brandId || selectedKeywordIds.size === 0) return;
    const ids = Array.from(selectedKeywordIds);

    setIsBulkPending(true);
    const outcomes = await Promise.all(
      ids.map((id) =>
        deleteKeywordAsync({ id, brandId })
          .then(() => ({ id, ok: true as const }))
          .catch(() => ({ id, ok: false as const })),
      ),
    );
    setIsBulkPending(false);

    const failedIds = outcomes.filter((o) => !o.ok).map((o) => o.id);
    const succeededIds = outcomes.filter((o) => o.ok).map((o) => o.id);

    setSelectedKeywordIds((prev) => {
      const next = new Set(prev);
      for (const id of succeededIds) next.delete(id);
      return next;
    });

    if (failedIds.length === 0) {
      const n = ids.length;
      toast({
        title: "Removed from Rank Tracker",
        description: `${n} keyword${n !== 1 ? "s" : ""} removed from tracking.`,
      });
    } else {
      const failedName = keywordTextById.get(failedIds[0]) ?? "a keyword";
      toast({
        title: "Couldn't remove every keyword",
        description: `"${failedName}" couldn't be removed from tracking.`,
        variant: "destructive",
      });
    }
  }, [brandId, selectedKeywordIds, deleteKeywordAsync, keywordTextById]);

  // The reachable-horizontal-scroll affordance (FR-10). `tableElRef.current.parentElement` is the
  // horizontally-scrollable div `Table` (ui/table.tsx) renders around every table -- reached through
  // the forwarded `<table>` ref rather than by editing that shared primitive, which sits outside this
  // task's freeze.
  const tableElRef = useRef<HTMLTableElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const scrollEl = tableElRef.current?.parentElement;
    if (!scrollEl) return;
    const overflow = scrollEl.scrollWidth - scrollEl.clientWidth;
    setCanScrollLeft(scrollEl.scrollLeft > 4);
    setCanScrollRight(overflow > 4 && scrollEl.scrollLeft < overflow - 4);
  }, []);

  useEffect(() => {
    const scrollEl = tableElRef.current?.parentElement;
    if (!scrollEl) return;
    updateScrollState();
    scrollEl.addEventListener("scroll", updateScrollState, { passive: true });
    const ro = new ResizeObserver(updateScrollState);
    ro.observe(scrollEl);
    if (tableElRef.current) ro.observe(tableElRef.current);
    return () => {
      scrollEl.removeEventListener("scroll", updateScrollState);
      ro.disconnect();
    };
    // Re-measure whenever the rendered row set can change the table's width or height.
  }, [updateScrollState, sortedProducts, expandedProducts, expandedThemes, days.length]);

  const scrollTableBy = (direction: 1 | -1) => {
    const scrollEl = tableElRef.current?.parentElement;
    if (!scrollEl) return;
    scrollEl.scrollBy({ left: direction * scrollEl.clientWidth * 0.7, behavior: "smooth" });
  };

  // A viewer is not offered any tracking-changing control, but every other part of the table --
  // the data, the filters, the search box -- renders exactly as it does for anyone else (Failure
  // paths table, `useViewerReadOnly()`). Both ThemeRow and KeywordRow (WEB-005, frozen) render their
  // Checkbox unconditionally with no prop to omit it, so this scopes a CSS rule to this table's own
  // wrapper instead of forking either row.
  const showBulkBar = !viewerReadOnly && selectedKeywordIds.size > 0;

  return (
    // AUT-425: the provider is what lets the pinned bar and the table's sticky header stack. The
    // bar registers its height; the shared `Table` below reads it and pins column labels directly
    // underneath, so neither covers the other (`hooks/useStickyToolbar.tsx`).
    <StickyToolbarProvider>
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h2 className="text-2xl font-semibold leading-8 text-foreground">Ranks by product</h2>
      </div>

      {/* Sits between the heading and the search bar, and **pinned** (AUT-425).
          It was deliberately unpinned in the design-conformance pass -- "a tinted inline block
          rather than a full-bleed sticky strip" -- but the design reference pins it: the
          prototype's own `BulkActionBar` is sticky (`useStickyToolbarOffset`, `top: stickyTop`).
          Selecting keywords and then scrolling to find the rows they are on left the actions
          behind, which is the gesture the bar exists for. */}
      {showBulkBar && (
        <BulkActionBar count={selectedKeywordIds.size} onClear={clearSelection} entityLabel="keyword" sticky>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="text-xs h-7 gap-1" disabled={isBulkPending}>
                Bulk Actions
                <ChevronDown className="h-3 w-3" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem onClick={handleBulkStopTracking} disabled={isBulkPending}>
                Stop tracking
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </BulkActionBar>
      )}

      {/* Toolbar row: search, Filters, the count line, then the window picker pinned to the right
          edge with ml-auto -- one row, matching the mockup (items 3+6 of the design-conformance
          pass). Reading ranks is not a privileged act (`rank-tracker/J1` guardrail), so the picker
          renders here unconditionally regardless of `useViewerReadOnly()`; only the tracking- and
          report-generating controls up in the page header are withheld from a viewer. */}
      <SearchFilterBar
        search={search}
        onSearchChange={setSearch}
        placeholder="Search products or keywords…"
        searchWidth="w-80 shrink-0"
      >
        <FilterDropdown
          themeOptions={themeFilterOptions}
          advertisingOptions={advertisingFilterOptions}
          activeFilters={activeFilters}
          onFiltersChange={setActiveFilters}
        />

        {!isLoading && !isError && (
          <p className="text-sm text-muted-foreground whitespace-nowrap">
            Showing {sortedProducts.length} product{sortedProducts.length !== 1 ? "s" : ""} ·{" "}
            {totalVisibleKeywords} tracked keyword{totalVisibleKeywords !== 1 ? "s" : ""}
          </p>
        )}

        {/* FR-6: the Rank-type toggle sits in the toolbar immediately LEFT of the date-range
            dropdown. Both are pushed to the right edge as one group (`ml-auto`); the toggle still
            renders when no `windowPicker` is supplied (the older test fixtures). */}
        <div className="ml-auto flex items-center gap-3">
          <RankTypeToggle
            value={rankType}
            onChange={setRankType}
            calloutOpen={calloutOpen}
            onCalloutDismiss={dismissCallout}
          />
          {windowPicker}
        </div>
      </SearchFilterBar>

      {/* FR-9: the heat-scale legend, below the toolbar and above the table. A pure function of the
          current view -- both scales and the two cell treatments are always shown, with one extra
          note per view. */}
      <RankLegend rankType={rankType} />

      <div className={cn("relative w-full", viewerReadOnly && "rank-heatmap-viewer")}>
        {viewerReadOnly && (
          <style>{`.rank-heatmap-viewer [role="checkbox"] { display: none; }`}</style>
        )}
        {/* table-fixed: item 9's 340px product-name column only binds under a fixed table layout --
            under the default `auto` layout a cell's `w-[340px]` is advisory and a long product name
            still widens the column past it. Every column now carries its own width on this table's
            first row (`HeatmapHeaderRow`), which is what `table-fixed` requires -- a column left
            without one would instead take a share of whatever space the table's own width leaves
            over. The table keeps `w-full` from its base class list; `table-fixed` does not collapse
            it into the scrollport when the summed column widths exceed the container -- the
            explicit column widths win and the table overflows exactly as it did before, which is
            what keeps FR-10's horizontal scroll reachable. */}
        {/* Item 1 of the design-conformance pass: the mockup's <Table> carries border-t border-border;
            ours had none. */}
        <Table ref={tableElRef} className="table-fixed border-t border-border">
          <TableHeader>
            <HeatmapHeaderRow
              days={days}
              sortDirection={sortDirection}
              onSortByProduct={toggleSort}
              brandHasCustomCutoffs={brandHasCustomCutoffs}
              rulesOpen={rulesOpen}
              onRulesOpenChange={setRulesOpen}
              brandId={brandId}
              brandName={brandName}
              adPushCutoffs={adPushCutoffsData}
              onAdPushDraftChange={setAdPushDraft}
            />
          </TableHeader>
          <TableBody>
            {isError ? (
              <TableRow>
                <TableCell colSpan={totalColumns} className="p-10">
                  <Alert variant="destructive" className="mx-auto max-w-md">
                    <AlertCircle className="h-4 w-4" aria-hidden="true" />
                    <AlertTitle>Couldn&apos;t load the rank data</AlertTitle>
                    <AlertDescription className="space-y-3">
                      <p>Something went wrong loading ranks for this brand.</p>
                      <Button variant="outline" size="sm" onClick={() => refetch()}>
                        Try again
                      </Button>
                    </AlertDescription>
                  </Alert>
                </TableCell>
              </TableRow>
            ) : isLoading ? (
              Array.from({ length: LOADING_SKELETON_ROW_COUNT }).map((_, index) => (
                <TableRow key={`rank-heatmap-skeleton-${index}`}>
                  <TableCell colSpan={totalColumns}>
                    <Skeleton className="h-14 w-full" />
                  </TableCell>
                </TableRow>
              ))
            ) : nothingTrackedYet ? (
              <TableRow>
                <TableCell colSpan={totalColumns} className="py-10 text-center text-muted-foreground">
                  Nothing is tracked yet. Add a keyword from a product&apos;s landscape to start
                  tracking its rank.
                </TableCell>
              </TableRow>
            ) : sortedProducts.length === 0 ? (
              // heroProductCount === 0 is the page's EmptyBrandState branch (WEB-008), not this
              // component's -- render neither of the two sentences above.
              heroProductCount === 0 ? null : (
                <TableRow>
                  <TableCell colSpan={totalColumns} className="py-10 text-center text-muted-foreground">
                    No tracked keywords match your search.
                  </TableCell>
                </TableRow>
              )
            ) : (
              sortedProducts.map((product) => (
                <Fragment key={product.id}>
                  <ProductRow
                    product={product}
                    days={days}
                    isExpanded={expandedProducts.has(product.id)}
                    onToggleExpand={() => toggleProduct(product.id)}
                    rankType={rankType}
                    onOpenDetail={() => onOpenDetail?.({ level: "product", product })}
                  />
                  {expandedProducts.has(product.id) &&
                    product.themes.map((theme) => {
                      const themeKey = `${product.id}:${theme.themeId ?? "none"}`;
                      const themeExpanded = expandedThemes.has(themeKey);
                      const themeKeywordIds = theme.keywords.map((k) => k.id);
                      const selectedInTheme = themeKeywordIds.filter((id) =>
                        selectedKeywordIds.has(id),
                      ).length;
                      const themeSelected: boolean | "indeterminate" =
                        selectedInTheme === 0
                          ? false
                          : selectedInTheme === themeKeywordIds.length
                            ? true
                            : "indeterminate";
                      // Narrowed by search and/or the two filter groups this render -- `undefined`
                      // (the plain "(N keywords)" form) whenever this theme's full membership is
                      // still showing.
                      const visibleKeywordCount =
                        theme.keywords.length < theme.keywordCount ? theme.keywords.length : undefined;

                      return (
                        <Fragment key={themeKey}>
                          <ThemeRow
                            theme={theme}
                            days={days}
                            isExpanded={themeExpanded}
                            onToggleExpand={() => toggleTheme(themeKey)}
                            selected={viewerReadOnly ? undefined : themeSelected}
                            onSelectAllChange={
                              viewerReadOnly
                                ? undefined
                                : (checked) => toggleThemeSelection(themeKeywordIds, checked)
                            }
                            visibleKeywordCount={visibleKeywordCount}
                            rankType={rankType}
                            onOpenDetail={() => onOpenDetail?.({ level: "theme", product, theme })}
                          />
                          {themeExpanded &&
                            theme.keywords.map((keyword) => (
                              <KeywordRow
                                key={keyword.id}
                                keyword={keyword}
                                days={days}
                                selected={viewerReadOnly ? undefined : selectedKeywordIds.has(keyword.id)}
                                onSelectChange={
                                  viewerReadOnly
                                    ? undefined
                                    : (checked) => toggleKeywordSelection(keyword.id, checked)
                                }
                                rankType={rankType}
                                cutoffs={effectiveAdPushCutoffs}
                                onOpenDetail={() =>
                                  onOpenDetail?.({ level: "keyword", keyword, theme, product })
                                }
                              />
                            ))}
                        </Fragment>
                      );
                    })}
                </Fragment>
              ))
            )}
          </TableBody>
        </Table>

        <TableScrollArrows
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
          onScrollLeft={() => scrollTableBy(-1)}
          onScrollRight={() => scrollTableBy(1)}
        />
      </div>
    </div>
    </StickyToolbarProvider>
  );
}
